import React, { useMemo, useState } from 'react'
import { Avatar, Tabs } from 'antd'
import TabPane from 'antd/lib/tabs/TabPane'
import { CaretLeft, CaretRight } from '@phosphor-icons/react'
import CustomModal from 'components/global/CustomModal'
import Spinner from 'components/Spinner'
import { leagueAttendanceStore } from 'state/league/leagueAttendance'
import { leagueEventStore } from 'state/league/leagueEvent'
import {
  useGetLeagueParticipants,
  LeagueParticipant,
} from 'api/league/LeagueAttendance/leagueAttendance'
import { getNameSignature } from 'helper/utility'

const WEEK_DAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT']

const calculateAge = (dob: string): number => {
  const birthDate = new Date(dob)
  const today = new Date()
  let age = today.getFullYear() - birthDate.getFullYear()
  const m = today.getMonth() - birthDate.getMonth()
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--
  return age
}

const getStatusInfo = (
  checkInStatus?: string | null,
  checkOutStatus?: string | null,
): { label: string; color: string } => {
  const ci = checkInStatus || 'no-status'
  const co = checkOutStatus
  if (ci === 'checked-in' && (!co || co === 'pending')) return { label: 'Checked-in', color: '#F1F8E7' }
  if (ci === 'checked-in' && co === 'checked-out') return { label: 'Checked-out', color: '#FFE2E0' }
  if (ci === 'checked-in' && co === 'request-check-out') return { label: 'Check-out pending', color: '#FEF4E8' }
  if (ci === 'droped-in') return { label: 'Dropped-in', color: '#E8E9F7' }
  if (ci === 'request-check-in') return { label: 'Check-in pending', color: '#FEF4E8' }
  return { label: 'No status', color: '#F6F7F8' }
}

type ParticipantWithType = LeagueParticipant & {
  participantType: 'kid' | 'guardian' | 'authorizedAdult' | 'dropInAttendee'
}

const flattenParticipants = (data: any): ParticipantWithType[] => {
  if (!data) return []
  return [
    ...(data.kids || []).map((p: LeagueParticipant) => ({ ...p, participantType: 'kid' as const })),
    ...(data.guardians || []).map((p: LeagueParticipant) => ({ ...p, participantType: 'guardian' as const })),
    ...(data.authorizedAdults || []).map((p: LeagueParticipant) => ({ ...p, participantType: 'authorizedAdult' as const })),
    ...(data.dropinAttendees || []).map((p: LeagueParticipant) => ({ ...p, participantType: 'dropInAttendee' as const })),
  ]
}

// ─── Today Tab ───────────────────────────────────────────────────────────────

const TicketsTodayTab: React.FC<{ leagueId: string }> = ({ leagueId }) => {
  const today = useMemo(() => {
    const d = new Date()
    d.setHours(0, 0, 0, 0)
    return d.toISOString()
  }, [])

  const { data, isLoading } = useGetLeagueParticipants({ leagueId, date: today })
  const participants = useMemo(() => flattenParticipants(data), [data])

  const checkedInCount = useMemo(
    () => participants.filter(p =>
      p.checkInStatus === 'checked-in' || p.checkInStatus === 'droped-in'
    ).length,
    [participants],
  )

  if (isLoading) {
    return <div className="flex justify-center py-40px"><Spinner /></div>
  }

  return (
    <div>
      <div className="flex justify-between align-center py-12px border-btm mb-4px">
        <span className="text-13 w-500">{participants.length} attendees</span>
        <span className="text-13 w-600" style={{ color: '#5BA54A' }}>
          {checkedInCount} checked-in
        </span>
      </div>

      {participants.length === 0 ? (
        <p className="py-20px text-14 w-400">No participants for today</p>
      ) : (
        participants.map((p, i) => {
          const status = getStatusInfo(p.checkInStatus, p.checkOutStatus)
          return (
            <div
              key={p._id}
              className={`flex align-center py-14px gap-14px ${i < participants.length - 1 ? 'border-btm' : ''}`}
            >
              <Avatar size={42}>
                {getNameSignature(`${p.firstName} ${p.lastName}`)}
              </Avatar>
              <div className="flex-1">
                <p className="text-14 w-500">{p.firstName} {p.lastName}</p>
                <div
                  className="flex align-center gap-6px mt-5px px-10px py-3px radius-20"
                  style={{ backgroundColor: status.color, display: 'inline-flex' }}
                >
                  <span className="text-11 w-500">{status.label}</span>
                </div>
              </div>
            </div>
          )
        })
      )}
    </div>
  )
}

// ─── History Tab ─────────────────────────────────────────────────────────────

const TicketsHistoryTab: React.FC<{ leagueId: string }> = ({ leagueId }) => {
  const [weekStart, setWeekStart] = useState(() => {
    const d = new Date()
    d.setDate(d.getDate() - d.getDay())
    d.setHours(0, 0, 0, 0)
    return d
  })

  const [selectedDate, setSelectedDate] = useState(() => {
    const d = new Date()
    d.setHours(0, 0, 0, 0)
    return d
  })

  const weekDates = useMemo(() =>
    Array.from({ length: 7 }, (_, i) => {
      const d = new Date(weekStart)
      d.setDate(weekStart.getDate() + i)
      return d
    }),
    [weekStart],
  )

  const monthYear = useMemo(
    () => weekDates[3].toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
    [weekDates],
  )

  const prevWeek = () => {
    const d = new Date(weekStart)
    d.setDate(d.getDate() - 7)
    setWeekStart(d)
  }

  const nextWeek = () => {
    const d = new Date(weekStart)
    d.setDate(d.getDate() + 7)
    setWeekStart(d)
  }

  const { data, isLoading } = useGetLeagueParticipants({
    leagueId,
    date: selectedDate.toISOString(),
  })
  const participants = useMemo(() => flattenParticipants(data), [data])

  return (
    <div>
      {/* Week calendar */}
      <div className="flex align-center justify-between py-10px">
        <button className="btn-borderless p-5px" onClick={prevWeek}>
          <CaretLeft size={16} />
        </button>
        <span className="text-14 w-600">{monthYear}</span>
        <button className="btn-borderless p-5px" onClick={nextWeek}>
          <CaretRight size={16} />
        </button>
      </div>

      <div className="flex justify-between mb-16px">
        {weekDates.map((d, i) => {
          const isSelected = d.toDateString() === selectedDate.toDateString()
          return (
            <div
              key={i}
              className="flex flex-col align-center pointer"
              onClick={() => setSelectedDate(d)}
            >
              <span className="text-11 w-500" style={{ color: '#908F98' }}>
                {WEEK_DAYS[i]}
              </span>
              <div
                className="flex align-center justify-center mt-4px"
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: '50%',
                  backgroundColor: isSelected ? '#F5A623' : 'transparent',
                  cursor: 'pointer',
                }}
              >
                <span
                  className="text-14 w-500"
                  style={{ color: isSelected ? '#fff' : '#1A1A2E' }}
                >
                  {d.getDate()}
                </span>
              </div>
            </div>
          )
        })}
      </div>

      {/* Summary bar */}
      <div className="flex justify-between align-center py-12px border-btm mb-4px">
        <span className="text-13 w-500">{participants.length} attendees</span>
        <span className="text-13 w-500" style={{ color: '#908F98' }}>
          {selectedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
        </span>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-40px"><Spinner /></div>
      ) : participants.length === 0 ? (
        <p className="py-20px text-14 w-400">No participants for this date</p>
      ) : (
        participants.map((p, i) => (
          <div
            key={p._id}
            className={`flex align-center py-14px gap-14px ${i < participants.length - 1 ? 'border-btm' : ''}`}
          >
            <Avatar size={42}>
              {getNameSignature(`${p.firstName} ${p.lastName}`)}
            </Avatar>
            <div>
              <p className="text-14 w-500">{p.firstName} {p.lastName}</p>
              {p.dob && (
                <p className="text-12 mt-3px" style={{ color: '#908F98' }}>
                  {calculateAge(p.dob)} y.o.
                </p>
              )}
            </div>
          </div>
        ))
      )}
    </div>
  )
}

// ─── Modal ────────────────────────────────────────────────────────────────────

const LeagueTicketsModal: React.FC = () => {
  const { ticketsModalOpen, setTicketsModalOpen } = leagueAttendanceStore(store => ({
    ticketsModalOpen: store.ticketsModalOpen,
    setTicketsModalOpen: store.setTicketsModalOpen,
  }))
  const { leagueData } = leagueEventStore(store => ({ leagueData: store.leagueData }))
  const leagueId = leagueData?._id || ''

  return (
    <CustomModal
      openModal={ticketsModalOpen}
      isCloseModalCustom
      closeModal={() => setTicketsModalOpen(false)}
      title="Tickets"
      width={560}
      footer={false}
    >
      <Tabs defaultActiveKey="today">
        <TabPane tab="Today" key="today">
          <TicketsTodayTab leagueId={leagueId} />
        </TabPane>
        <TabPane tab="History" key="history">
          <TicketsHistoryTab leagueId={leagueId} />
        </TabPane>
      </Tabs>
    </CustomModal>
  )
}

export default LeagueTicketsModal
